/**
 * Is this the same condition, written by two different people?
 *
 * ⚠️ THIS IS SPELLING TOLERANCE, NOT CLINICAL EQUIVALENCE. It exists because a clinician typing
 * their own diagnosis into a box produces "Accute cervical strain" and "acute cervical strain",
 * and treating those as two different diagnoses is obviously wrong to everyone looking at the
 * screen. What it cannot do is know that "MI" and "myocardial infarction" are the same thing, or
 * that "strep throat" is "streptococcal pharyngitis" — that is a vocabulary problem and it needs
 * a lookup or a model, not string distance.
 *
 * So nothing safety-bearing may be built on a false return. It decides presentation only: whether
 * the clinician's diagnosis is shown as its own card or as a mark on the matching one. Both
 * readings are correct enough to put in front of a doctor, which is why a cheap local comparison
 * is the right tool here and a "the model missed your diagnosis" warning was not.
 */

/** Case, punctuation and spacing are never the difference between two diagnoses. */
export const normalizeCondition = (value) =>
  (value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const bigrams = (value) => {
  const set = new Set();
  for (let i = 0; i < value.length - 1; i += 1) {
    set.add(value.slice(i, i + 2));
  }
  return set;
};

/**
 * Sørensen–Dice over character bigrams: 1 is identical, 0 shares nothing.
 * A transposed or doubled letter costs very little, a different word costs a lot.
 */
export const conditionSimilarity = (a, b) => {
  const left = normalizeCondition(a);
  const right = normalizeCondition(b);
  if (!left || !right) return 0;
  if (left === right) return 1;
  if (left.length < 2 || right.length < 2) return 0;

  const leftGrams = bigrams(left);
  const rightGrams = bigrams(right);
  let shared = 0;
  rightGrams.forEach((gram) => {
    if (leftGrams.has(gram)) shared += 1;
  });

  return (2 * shared) / (leftGrams.size + rightGrams.size);
};

/**
 * Tuned to swallow typos without merging neighbours: "acute cholecystitis" and "acute
 * cholangitis" stay apart, which matters more than catching every misspelling.
 */
export const SAME_CONDITION_THRESHOLD = 0.82;

export const isSameCondition = (a, b) =>
  conditionSimilarity(a, b) >= SAME_CONDITION_THRESHOLD;

export default isSameCondition;
